import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import type { Team } from '@/types/entities'

interface TeamDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    team: Team | null
    onConfirm: () => void
    isLoading?: boolean
}

export function TeamDeleteDialog({
    open,
    onOpenChange,
    team,
    onConfirm,
    isLoading = false,
}: TeamDeleteDialogProps) {
    const { t } = useTranslation()
    if (!team) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('teams.deleteTeamTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('teams.confirmDelete')} "{team.teamName}"? {t('teams.deleteWarning')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('teams.permanentAction')}
                        </AlertDescription>
                    </Alert>

                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div><strong>{t('teams.teamName')}:</strong> {team.teamName}</div>
                            {team.description && (
                                <div><strong>{t('teams.description')}:</strong> {team.description}</div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('teams.deleting') : t('teams.deleteTeam')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
